import { noop, PageExitReason, shallowClone, elapsed, generateUUID, ONE_MINUTE, throttle, clocksNow, clocksOrigin, relativeToClocks, timeStampNow, display, looksLikeRelativeTime, setInterval, clearInterval, setTimeout, Observable, createContextManager } from '@datadog/browser-core';
import { ViewLoadingType } from '../../rawRumEvent.types';
import { trackViewEventCounts } from './trackViewEventCounts';
import { trackInitialViewMetrics } from './viewMetrics/trackInitialViewMetrics';
import { trackCommonViewMetrics } from './viewMetrics/trackCommonViewMetrics';
import { onBFCacheRestore } from './bfCacheSupport';
import { trackBfcacheMetrics } from './viewMetrics/trackBfcacheMetrics';
export var THROTTLE_VIEW_UPDATE_PERIOD = 3000;
export var SESSION_KEEP_ALIVE_INTERVAL = 5 * ONE_MINUTE; // Some events or metrics can be captured after the end of the view. To avoid missing those;
// an arbitrary delay is added for stopping their tracking after the view ends.
//
// Ideally, we would not stop and keep tracking events or metrics until the end of the session.
// But this might have a small performance impact if there are many many views.
// So let's have a fairly short delay improving the situation in most cases and avoid impacting performances too much.

export var KEEP_TRACKING_AFTER_VIEW_DELAY = 5 * ONE_MINUTE;
export function trackViews(location, lifeCycle, domMutationObservable, windowOpenObservable, configuration, locationChangeObservable, areViewsTrackedAutomatically, initialViewOptions) {
  var activeViews = new Set();
  var currentView = startNewView(ViewLoadingType.INITIAL_LOAD, clocksOrigin(), initialViewOptions);
  var stopOnBFCacheRestore;
  startViewLifeCycle();
  var locationChangeSubscription;

  if (areViewsTrackedAutomatically) {
    locationChangeSubscription = renewViewOnLocationChange(locationChangeObservable);

    if (configuration.trackBfcacheViews) {
      stopOnBFCacheRestore = onBFCacheRestore(configuration, function (pageshowEvent) {
        currentView.end();
        var startClocks = relativeToClocks(pageshowEvent.timeStamp);
        currentView = startNewView(ViewLoadingType.BF_CACHE, startClocks, undefined);
      });
    }
  }

  function startNewView(loadingType, startClocks, viewOptions) {
    var newlyCreatedView = newView(lifeCycle, domMutationObservable, windowOpenObservable, configuration, location, loadingType, startClocks, viewOptions);
    activeViews.add(newlyCreatedView);
    newlyCreatedView.stopObservable.subscribe(function () {
      activeViews["delete"](newlyCreatedView);
    });
    return newlyCreatedView;
  }

  function startViewLifeCycle() {
    lifeCycle.subscribe(10
    /* LifeCycleEventType.SESSION_RENEWED */
    , function () {
      // Renew view on session renewal
      currentView = startNewView(ViewLoadingType.ROUTE_CHANGE, undefined, {
        name: currentView.name,
        service: currentView.service,
        version: currentView.version,
        context: currentView.contextManager.getContext()
      });
    });
    lifeCycle.subscribe(9
    /* LifeCycleEventType.SESSION_EXPIRED */
    , function () {
      currentView.end({
        sessionIsActive: false
      });
    });
  }

  function renewViewOnLocationChange(locationChangeObservable) {
    return locationChangeObservable.subscribe(function (_ref) {
      var oldLocation = _ref.oldLocation,
          newLocation = _ref.newLocation;

      if (areDifferentLocation(oldLocation, newLocation)) {
        currentView.end();
        currentView = startNewView(ViewLoadingType.ROUTE_CHANGE);
      }
    });
  }

  return {
    addTiming: function addTiming(name) {
      var time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : timeStampNow();
      currentView.addTiming(name, time);
    },
    startView: function startView(options, startClocks) {
      currentView.end({
        endClocks: startClocks
      });
      currentView = startNewView(ViewLoadingType.ROUTE_CHANGE, startClocks, options);
    },
    setViewContext: function setViewContext(context) {
      currentView.contextManager.setContext(context);
    },
    setViewContextProperty: function setViewContextProperty(key, value) {
      currentView.contextManager.setContextProperty(key, value);
    },
    setViewName: function setViewName(name) {
      currentView.setViewName(name);
    },
    getViewContext: function getViewContext() {
      return currentView.contextManager.getContext();
    },
    stop: function stop() {
      if (locationChangeSubscription) {
        locationChangeSubscription.unsubscribe();
      }

      if (stopOnBFCacheRestore) {
        stopOnBFCacheRestore();
      }

      currentView.end();
      activeViews.forEach(function (view) {
        return view.stop();
      });
    }
  };
}

function newView(lifeCycle, domMutationObservable, windowOpenObservable, configuration, initialLocation, loadingType) {
  var startClocks = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : clocksNow();
  var viewOptions = arguments.length > 7 ? arguments[7] : undefined;
  // Setup initial values
  var id = generateUUID();
  var stopObservable = new Observable();
  var customTimings = {};
  var documentVersion = 0;
  var endClocks;
  var location = shallowClone(initialLocation);
  var contextManager = createContextManager();
  var sessionIsActive = true;
  var name = viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.name;
  var service = (viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.service) || configuration.service;
  var version = (viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.version) || configuration.version;
  var context = viewOptions === null || viewOptions === void 0 ? void 0 : viewOptions.context;

  if (context) {
    contextManager.setContext(context);
  }

  var viewCreatedEvent = {
    id: id,
    name: name,
    startClocks: startClocks,
    service: service,
    version: version,
    context: context
  };
  lifeCycle.notify(1
  /* LifeCycleEventType.BEFORE_VIEW_CREATED */
  , viewCreatedEvent);
  lifeCycle.notify(2
  /* LifeCycleEventType.VIEW_CREATED */
  , viewCreatedEvent); // Update the view every time the measures are changing

  var _throttle = throttle(triggerViewUpdate, THROTTLE_VIEW_UPDATE_PERIOD, {
    leading: false
  }),
      throttled = _throttle.throttled,
      cancelScheduleViewUpdate = _throttle.cancel;

  var _trackCommonViewMetri = trackCommonViewMetrics(lifeCycle, domMutationObservable, windowOpenObservable, configuration, scheduleViewUpdate, loadingType, startClocks),
      setLoadEvent = _trackCommonViewMetri.setLoadEvent,
      setViewEnd = _trackCommonViewMetri.setViewEnd,
      stopCommonViewMetricsTracking = _trackCommonViewMetri.stop,
      stopINPTracking = _trackCommonViewMetri.stopINPTracking,
      getCommonViewMetrics = _trackCommonViewMetri.getCommonViewMetrics;

  var _ref2 = loadingType === ViewLoadingType.INITIAL_LOAD ? trackInitialViewMetrics(configuration, startClocks, setLoadEvent, scheduleViewUpdate) : {
    stop: noop,
    initialViewMetrics: {}
  },
      stopInitialViewMetricsTracking = _ref2.stop,
      initialViewMetrics = _ref2.initialViewMetrics; // Start BFCache-specific metrics when restoring from BFCache


  if (loadingType === ViewLoadingType.BF_CACHE) {
    trackBfcacheMetrics(startClocks, initialViewMetrics, scheduleViewUpdate);
  }

  var _trackViewEventCounts = trackViewEventCounts(lifeCycle, id, scheduleViewUpdate),
      stopEventCountsTracking = _trackViewEventCounts.stop,
      eventCounts = _trackViewEventCounts.eventCounts; // Session keep alive


  var keepAliveIntervalId = setInterval(triggerViewUpdate, SESSION_KEEP_ALIVE_INTERVAL);
  var pageMayExitSubscription = lifeCycle.subscribe(11
  /* LifeCycleEventType.PAGE_MAY_EXIT */
  , function (pageMayExitEvent) {
    if (pageMayExitEvent.reason === PageExitReason.UNLOADING) {
      triggerViewUpdate();
    }
  }); // Initial view update

  triggerViewUpdate(); // View context update should always be throttled

  contextManager.changeObservable.subscribe(scheduleViewUpdate);

  function triggerBeforeViewUpdate() {
    lifeCycle.notify(3
    /* LifeCycleEventType.BEFORE_VIEW_UPDATED */
    , {
      id: id,
      name: name,
      context: contextManager.getContext(),
      startClocks: startClocks,
      sessionIsActive: sessionIsActive
    });
  }

  function scheduleViewUpdate() {
    triggerBeforeViewUpdate();
    throttled();
  }

  function triggerViewUpdate() {
    cancelScheduleViewUpdate();
    triggerBeforeViewUpdate();
    documentVersion += 1;
    var currentEnd = endClocks === undefined ? timeStampNow() : endClocks.timeStamp;
    lifeCycle.notify(4
    /* LifeCycleEventType.VIEW_UPDATED */
    , {
      customTimings: customTimings,
      documentVersion: documentVersion,
      id: id,
      name: name,
      service: service,
      version: version,
      context: contextManager.getContext(),
      loadingType: loadingType,
      location: location,
      startClocks: startClocks,
      commonViewMetrics: getCommonViewMetrics(),
      initialViewMetrics: initialViewMetrics,
      duration: elapsed(startClocks.timeStamp, currentEnd),
      isActive: endClocks === undefined,
      sessionIsActive: sessionIsActive,
      eventCounts: eventCounts
    });
  }

  return {
    get name() {
      return name;
    },

    service: service,
    version: version,
    contextManager: contextManager,
    stopObservable: stopObservable,
    end: function end() {
      var _this = this;

      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _a, _b;

      if (endClocks) {
        // view already ended
        return;
      }

      endClocks = (_a = options.endClocks) !== null && _a !== void 0 ? _a : clocksNow();
      sessionIsActive = (_b = options.sessionIsActive) !== null && _b !== void 0 ? _b : true;
      lifeCycle.notify(5
      /* LifeCycleEventType.VIEW_ENDED */
      , {
        endClocks: endClocks
      });
      lifeCycle.notify(6
      /* LifeCycleEventType.AFTER_VIEW_ENDED */
      , {
        endClocks: endClocks
      });
      clearInterval(keepAliveIntervalId);
      setViewEnd(endClocks.relative);
      stopCommonViewMetricsTracking();
      pageMayExitSubscription.unsubscribe();
      triggerViewUpdate();
      setTimeout(function () {
        _this.stop();
      }, KEEP_TRACKING_AFTER_VIEW_DELAY);
    },
    stop: function stop() {
      stopInitialViewMetricsTracking();
      stopEventCountsTracking();
      stopINPTracking();
      stopObservable.notify();
    },
    addTiming: function addTiming(name, time) {
      if (endClocks) {
        return;
      }

      var relativeTime = looksLikeRelativeTime(time) ? time : elapsed(startClocks.timeStamp, time);
      customTimings[sanitizeTiming(name)] = relativeTime;
      scheduleViewUpdate();
    },
    setViewName: function setViewName(updatedName) {
      name = updatedName;
      triggerViewUpdate();
    }
  };
}
/**
 * Timing name is used as facet path that must contain only letters, digits, or the characters - _ . @ $
 */


function sanitizeTiming(name) {
  var sanitized = name.replace(/[^a-zA-Z0-9-_.@$]/g, '_');

  if (sanitized !== name) {
    display.warn("Invalid timing name: ".concat(name, ", sanitized to: ").concat(sanitized));
  }

  return sanitized;
}

function areDifferentLocation(currentLocation, otherLocation) {
  return currentLocation.pathname !== otherLocation.pathname || !isHashAnAnchor(otherLocation.hash) && getPathFromHash(otherLocation.hash) !== getPathFromHash(currentLocation.hash);
}

function isHashAnAnchor(hash) {
  var correspondingId = hash.substring(1); // check if the correspondingId is empty because on Firefox an empty string passed to getElementById() prints a consol warning

  return correspondingId !== '' && !!document.getElementById(correspondingId);
}

function getPathFromHash(hash) {
  var index = hash.indexOf('?');
  return index < 0 ? hash : hash.slice(0, index);
}